import { Box, Skeleton, SkeletonGroup } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

export type ListRowsSkeletonProps = Omittable<{
  /** How many rows to lay. */
  rows?: number;
  /** Row rung in px. */
  rowHeight?: number;
  /** The glyph seat at the row's start: a 20px circle (a person), a 16px
   *  tile (an icon-kind entity or a checkbox), or none (a text list). */
  leading?: "circle" | "tile" | "none";
  /** A second, shorter bar under the label — for two-line rows. */
  subtitled?: boolean;
  /** Reserve a short value bar at the row's end. */
  trailing?: boolean;
  /** Gate the whole block behind the ~200ms first-paint fade. */
  delayed?: boolean;
}>;

/**
 * Anonymous list rows: a leading glyph seat, a label-length bar and an
 * optional trailing value, at the rung the settled rows will take. The
 * stand-in for any single-line list whose exact rows are unknown — a
 * popover's grants, a panel's associated items, an option list. Every
 * surface that loads such a list uses this rather than a centered spinner:
 * a spinner says "wait", rows say "a list is coming here".
 */
export function ListRowsSkeleton({
  rows = 3,
  rowHeight = 36,
  leading = "tile",
  subtitled = false,
  trailing = false,
  delayed = false,
}: ListRowsSkeletonProps) {
  const body = Array.from({ length: rows }, (_, index) => (
    <Box key={index} direction="row" align="center" gap="sm" height={rowHeight}>
      {leading === "circle" ? (
        <Skeleton circle width={20} />
      ) : leading === "tile" ? (
        <Skeleton width={16} height={16} className="rounded-[4px]" />
      ) : null}
      <Box direction="column" gap="xs" grow minWidth={0}>
        <Skeleton height={12} vary={index} width={160} />
        {subtitled ? <Skeleton height={10} vary={index + 2} width={160} /> : null}
      </Box>
      {trailing ? <Skeleton height={12} width={48} /> : null}
    </Box>
  ));
  return delayed ? (
    <SkeletonGroup className="flex flex-col" data-slot="list-rows-skeleton" aria-hidden>
      {body}
    </SkeletonGroup>
  ) : (
    <Box direction="column" data-slot="list-rows-skeleton" aria-hidden>
      {body}
    </Box>
  );
}
